<script lang="ts">
  import { Link, navigate } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import { isAuthenticated } from '../../lib/stores/auth';
  import { confirm } from '../../lib/stores/confirmDialog';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import GoalEditor from '../../lib/components/activities/GoalEditor.svelte';
  import Ring from '../../lib/components/activities/Ring.svelte';
  import Bars from '../../lib/components/charts/Bars.svelte';
  import Chart from '../../lib/components/charts/Chart.svelte';
  import Readout from '../../lib/components/charts/Readout.svelte';
  import { yPct } from '../../lib/utils/chart';
  import { calendar, goalLeft, goalScope, measureAxis, measureValue, periodNow, type Goal, type GoalPeriod } from '../../lib/utils/insights';
  import { locale, month, monthDay, t } from '../../lib/i18n/index.ts';

  // Goal is one goal's last 12 periods, each with its amount and whether it
  // met the target it had (spec 006 FR-065), with editing and deleting.
  // The chart is in the shared chart language (spec 010 FR-033): a bar a
  // period with its target marked across it, read by hovering, tapping, or
  // the arrow keys.
  let { goalId }: { goalId: string } = $props();

  let goal = $state<Goal | null>(null);
  let history = $state<GoalPeriod[]>([]);
  let editing = $state(false);
  let error = $state('');

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  $effect(() => {
    void goalId;
    load();
  });

  async function load() {
    try {
      const all = await api.get<Goal[]>(`/me/goals?${calendar()}`);
      goal = all.find((g) => g.id === goalId) ?? null;
      if (!goal) {
        error = t('activitiesGoalGone');
        return;
      }
      history = await api.get<GoalPeriod[]>(`/me/goals/${goalId}/history?${calendar()}`);
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function remove() {
    if (!goal || !(await confirm(t('activitiesGoalDeleteConfirm')))) return;
    try {
      await api.delete(`/me/goals/${goalId}`);
      navigate('/activities/progress');
    } catch (e: any) {
      error = e.message;
    }
  }

  const MET = 'var(--theme-success)';
  const UNMET = 'var(--theme-overlay1)';
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let shown = $derived(hover ?? at);
  let axis = $derived(measureAxis(goal?.measure ?? 'count'));
  let scale = $derived(axis.scale(Math.max(...history.map((p) => Math.max(p.done, p.target)), 0) * axis.factor));
  let positions = $derived(history.map((_, i) => (i + 0.5) / history.length));

  // reading is a period's amount against its target: "Sep 21: 12.00 km of
  // 20.00 km, not met".
  function reading(p: GoalPeriod): string {
    return t('activitiesGoalReading', { period: label(p), done: measureValue(goal!.measure, p.done), target: measureValue(goal!.measure, p.target), met: p.met ? 'yes' : 'no' });
  }

  function label(p: GoalPeriod): string {
    const d = new Date(`${p.period_start}T12:00:00`);
    switch (goal?.period) {
      case 'year': return new Intl.DateTimeFormat(locale(), { year: 'numeric' }).format(d);
      case 'month': return month(d, true);
      default: return monthDay(d);
    }
  }
</script>

<div class="p-6 max-w-3xl w-full mx-auto space-y-4">
  <Link to="/activities/progress" class="text-sm hover:opacity-80"><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('activitiesProgress')}</Link>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if goal}
    <PageHeader title={t('activitiesGoalTitle', { period: goal.period })} />
    <div class="flex items-center gap-4">
      <Ring share={goal.done / goal.target} size={72} />
      <div>
        <p class="text-lg font-semibold">{periodNow(goal.period)} · {goalLeft(goal)}</p>
        <p class="text-sm text-text-muted">{measureValue(goal.measure, goal.done)} / {measureValue(goal.measure, goal.target)} · {goalScope(goal)}</p>
      </div>
    </div>
    <div class="flex gap-2">
      <button class="btn btn-secondary btn-sm" onclick={() => (editing = true)}>{t('activitiesGoalEditTarget')}</button>
      <button class="btn btn-secondary btn-sm" onclick={remove}>{t('appDelete')}</button>
    </div>
    <h2 class="font-semibold">{t('activitiesGoalLast12', { period: goal.period })}</h2>
    {#if history.length}
      <Readout title={shown != null ? reading(history[shown]) : t('activitiesGoalHint')} idle={shown == null}
        items={[{ label: t('activitiesGoalMet'), color: MET, mark: 'bar' }, { label: t('activitiesGoalNotMet'), color: UNMET, mark: 'bar' }, { label: t('activitiesGoalTarget'), color: 'var(--theme-text)' }]} />
      <Chart lo={scale.lo} hi={scale.hi} unit={axis.unit} yTicks={scale.ticks.map((tick) => ({ v: tick, text: axis.tick(tick, scale.step) }))}
        xTicks={history.map((p, i) => ({ at: positions[i], text: label(p) }))} xGrid={false} height="h-40" {positions} bind:at bind:hover
        label={t('activitiesGoalChartLabel')} valueText={shown != null ? reading(history[shown]) : t('activitiesGoalNonePicked')}>
        {#snippet overlay()}
          <Bars bars={history.map((p, i) => ({ at: positions[i], v: p.done * axis.factor, color: p.met ? MET : UNMET }))} width={0.6 / history.length} lo={scale.lo} hi={scale.hi} picked={at} />
          {#each history as p, i (p.period_start)}
            <span class="absolute h-0.5 pointer-events-none" aria-hidden="true"
              style="left: {positions[i] * 100}%; width: min({80 / history.length}%, 32px); transform: translate(-50%, -50%); top: {yPct(p.target * axis.factor, scale.lo, scale.hi)}%; background: var(--theme-text);"></span>
            {#if p.met}
              <i class="las la-check absolute text-xs pointer-events-none" aria-hidden="true"
                style="left: {positions[i] * 100}%; transform: translate(-50%, -100%); top: calc({yPct(Math.max(p.done, p.target) * axis.factor, scale.lo, scale.hi)}% - 2px); color: var(--theme-text);"></i>
            {/if}
          {/each}
        {/snippet}
      </Chart>
      <table class="sr-only">
        <caption>{t('activitiesGoalChartLabel')}</caption>
        <thead><tr><th>{t('activitiesGoalColPeriod')}</th><th>{t('activitiesGoalColAmount')}</th><th>{t('activitiesGoalTarget')}</th><th>{t('activitiesGoalMet')}</th></tr></thead>
        <tbody>
          {#each history as p (p.period_start)}
            <tr><td>{label(p)}</td><td>{measureValue(goal.measure, p.done)}</td><td>{measureValue(goal.measure, p.target)}</td><td>{p.met ? t('appYes') : t('appNo')}</td></tr>
          {/each}
        </tbody>
      </table>
    {/if}
    <p class="text-xs text-text-muted">{t('activitiesGoalFootnote')}</p>
  {/if}
</div>

{#if editing && goal}
  <GoalEditor {goal} onclose={() => (editing = false)} onsaved={() => { editing = false; load(); }} />
{/if}
